import createSelector from 'selectorator'
import { identity, pathOr } from 'ramda'
import { RootState } from 'src/state/types'
import { IDocument, IDocumentsState } from './types'
import { getClassKey } from './state'
import { NAMESPACE } from './constants'

const EMPTY_DOCUMENTS: IDocument[] = []
const EMPTY_TAGS: Record<string, string> = {}

/**
 * Get a slice of the documents API state
 * EG: getSlice('byClassKey')
 *
 * @param key Documents API state object key
 */
export const getSlice = <K extends keyof IDocumentsState>(key: K) =>
  createSelector<RootState, IDocumentsState[K]>([`api.${NAMESPACE}.${key}`], identity)

export const getByClassKey = getSlice('byClassKey')
export const getTags = getSlice('tags')

/**
 * Get documents for a specific class and classId
 */
export const getDocumentsByClass = createSelector<RootState, (className: string, classId: string) => IDocument[]>(
  [getByClassKey],
  byClassKey => (className, classId) =>
    pathOr(EMPTY_DOCUMENTS, [getClassKey(className, classId), 'documents'], byClassKey),
)

/**
 * Get tags for a specific document
 */
export const getDocumentTags = createSelector<RootState, (documentId: string) => Record<string, string>>(
  [getTags],
  tags => documentId => pathOr(EMPTY_TAGS, [documentId], tags),
)

/**
 * Get all tags for multiple documents
 */
export const getAllDocumentTags = getTags

/**
 * Get the ID of the last successfully removed document
 */
export const getLastRemovedDocumentId = getSlice('lastRemovedDocumentId')
